<script setup lang="ts">
  import { onBeforeUnmount, watch } from "vue";
  import { useEditor } from "@tiptap/vue-3";
  import StarterKit from "@tiptap/starter-kit";
  import { TableKit } from "@tiptap/extension-table";
  import Placeholder from "@tiptap/extension-placeholder";
  import { Markdown } from "@tiptap/markdown";
  import { TiptapContent, TiptapProvider, TiptapToolbar } from "@/components/episto-ui/tiptap";

  const props = defineProps<{ markdown: string; disabled?: boolean }>();
  const emit = defineEmits<{ change: [markdown: string | null] }>();

  let baseline = "";

  const editor = useEditor({
    content: props.markdown,
    contentType: "markdown",
    editable: !props.disabled,
    extensions: [
      StarterKit.configure({ link: { openOnClick: false } }),
      TableKit.configure({ table: { resizable: false } }),
      Placeholder.configure({ placeholder: "Objectives, target, hypotheses…" }),
      Markdown,
    ],
    onCreate: ({ editor: current }) => {
      baseline = current.getMarkdown();
    },
    onUpdate: ({ editor: current }) => {
      const markdown = current.getMarkdown();
      emit("change", markdown === baseline ? null : markdown);
    },
  });

  watch(
    () => props.markdown,
    (markdown) => {
      if (!editor.value) return;

      editor.value.commands.setContent(markdown, { contentType: "markdown", emitUpdate: false });
      baseline = editor.value.getMarkdown();
      emit("change", null);
    },
  );

  watch(
    () => props.disabled,
    (disabled) => editor.value?.setEditable(!disabled, false),
  );

  onBeforeUnmount(() => editor.value?.destroy());
</script>

<template>
  <TiptapProvider
    v-if="editor"
    :editor="editor"
    class="flex min-h-0 flex-1 flex-col">
    <div class="flex min-h-0 flex-1 flex-col">
      <TiptapToolbar class="shrink-0 flex-wrap border-b px-3 py-1" />
      <TiptapContent
        data-cy="survey-context-content"
        class="min-h-0 flex-1 overflow-y-auto px-4 py-4 [&_.ProseMirror]:min-h-full [&_.ProseMirror]:text-sm [&_.ProseMirror]:leading-relaxed [&_.ProseMirror]:text-foreground-secondary [&_h1]:mt-4 [&_h1]:text-base [&_h1]:font-semibold [&_h1]:text-primary [&_h2]:mt-4 [&_h2]:mb-0.5 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:text-primary [&_h2:first-child]:mt-0 [&_h3]:mt-3 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-primary [&_p+p]:mt-2 [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[0.9em] [&_pre]:my-2 [&_pre]:rounded-md [&_pre]:bg-muted [&_pre]:p-3 [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground [&_hr]:my-4 [&_a]:text-primary [&_a]:underline [&_table]:my-2 [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:px-2 [&_td]:py-1 [&_td]:align-top [&_th]:border [&_th]:bg-muted [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:font-medium [&_td_p]:m-0 [&_th_p]:m-0" />
    </div>
  </TiptapProvider>
</template>
